import { ApiFeatures } from './features';
import {
  Fee,
  Vendor,
  Announcement,
  AccessKey,
  UnitTransfer,
  BulkFeeRequest,
  OwnerNotification,
  MaintenanceSchedule,
} from '../../types';

export class ApiService extends ApiFeatures {
  // ============ VENDORS (S7.1) ============

  async getVendors(condoId?: string): Promise<Vendor[]> {
    const q = condoId ? `?condoId=${condoId}` : '';
    return this.request<Vendor[]>(`/vendors${q}`);
  }

  async createVendor(vendor: Omit<Vendor, 'id' | 'createdAt'>): Promise<Vendor> {
    return this.request<Vendor>('/vendors', { method: 'POST', body: JSON.stringify(vendor) });
  }

  async updateVendor(id: string, vendor: Partial<Vendor>): Promise<Vendor> {
    return this.request<Vendor>(`/vendors/${id}`, { method: 'PUT', body: JSON.stringify(vendor) });
  }

  async deleteVendor(id: string): Promise<void> {
    return this.request<void>(`/vendors/${id}`, { method: 'DELETE' });
  }

  async rateVendor(id: string, rating: number): Promise<Vendor> {
    return this.request<Vendor>(`/vendors/${id}/rate?rating=${rating}`, { method: 'PUT' });
  }

  // ============ ANNOUNCEMENTS (S7.2) ============

  async getAnnouncements(condoId?: string, publishedOnly = false): Promise<Announcement[]> {
    const params = new URLSearchParams();
    if (condoId) params.set('condoId', condoId);
    if (publishedOnly) params.set('publishedOnly', 'true');
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<Announcement[]>(`/announcements${q}`);
  }

  async createAnnouncement(a: Omit<Announcement, 'id' | 'createdAt'>): Promise<Announcement> {
    return this.request<Announcement>('/announcements', { method: 'POST', body: JSON.stringify(a) });
  }

  async updateAnnouncement(id: string, a: Partial<Announcement>): Promise<Announcement> {
    return this.request<Announcement>(`/announcements/${id}`, { method: 'PUT', body: JSON.stringify(a) });
  }

  async archiveAnnouncement(id: string): Promise<Announcement> {
    return this.request<Announcement>(`/announcements/${id}/archive`, { method: 'PUT' });
  }

  async deleteAnnouncement(id: string): Promise<void> {
    return this.request<void>(`/announcements/${id}`, { method: 'DELETE' });
  }

  // ============ BULK FEES (S8.1) ============

  async bulkCreateFees(req: BulkFeeRequest): Promise<Fee[]> {
    return this.request<Fee[]>('/fees/bulk', { method: 'POST', body: JSON.stringify(req) });
  }

  // ============ UNIT TRANSFERS (S8.2) ============

  async getUnitTransfers(condoId?: string, unitNumber?: string): Promise<UnitTransfer[]> {
    const params = new URLSearchParams();
    if (condoId) params.set('condoId', condoId);
    if (unitNumber) params.set('unitNumber', unitNumber);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<UnitTransfer[]>(`/unit-transfers${q}`);
  }

  async recordUnitTransfer(transfer: Omit<UnitTransfer, 'id' | 'createdAt'>): Promise<UnitTransfer> {
    return this.request<UnitTransfer>('/unit-transfers', { method: 'POST', body: JSON.stringify(transfer) });
  }

  // ============ ACCESS KEYS (S8.3) ============

  async getAccessKeys(condoId?: string): Promise<AccessKey[]> {
    const q = condoId ? `?condoId=${condoId}` : '';
    return this.request<AccessKey[]>(`/access-keys${q}`);
  }

  async createAccessKey(key: Omit<AccessKey, 'id' | 'createdAt'>): Promise<AccessKey> {
    return this.request<AccessKey>('/access-keys', { method: 'POST', body: JSON.stringify(key) });
  }

  async updateAccessKey(id: string, key: Partial<AccessKey>): Promise<AccessKey> {
    return this.request<AccessKey>(`/access-keys/${id}`, { method: 'PUT', body: JSON.stringify(key) });
  }

  async returnAccessKey(id: string): Promise<AccessKey> {
    return this.request<AccessKey>(`/access-keys/${id}/return`, { method: 'PUT' });
  }

  async reportAccessKeyLost(id: string): Promise<AccessKey> {
    return this.request<AccessKey>(`/access-keys/${id}/lost`, { method: 'PUT' });
  }

  async deleteAccessKey(id: string): Promise<void> {
    return this.request<void>(`/access-keys/${id}`, { method: 'DELETE' });
  }

  // ============ OWNER NOTIFICATIONS (S9.1) ============

  async getNotifications(condoId: string, activeOnly = true): Promise<OwnerNotification[]> {
    return this.request<OwnerNotification[]>(`/notifications?condoId=${condoId}&activeOnly=${activeOnly}`);
  }

  async createNotification(n: Omit<OwnerNotification, 'id' | 'createdAt' | 'active'>): Promise<OwnerNotification> {
    return this.request<OwnerNotification>('/notifications', { method: 'POST', body: JSON.stringify(n) });
  }

  async updateNotification(id: string, n: Partial<OwnerNotification>): Promise<OwnerNotification> {
    return this.request<OwnerNotification>(`/notifications/${id}`, { method: 'PUT', body: JSON.stringify(n) });
  }

  async deactivateNotification(id: string): Promise<OwnerNotification> {
    return this.request<OwnerNotification>(`/notifications/${id}/deactivate`, { method: 'PUT' });
  }

  async deleteNotification(id: string): Promise<void> {
    return this.request<void>(`/notifications/${id}`, { method: 'DELETE' });
  }

  // ============ MAINTENANCE SCHEDULES (S9.2) ============

  async getMaintenanceSchedules(condoId: string): Promise<MaintenanceSchedule[]> {
    return this.request<MaintenanceSchedule[]>(`/maintenance-schedules?condoId=${condoId}`);
  }

  async createMaintenanceSchedule(ms: Omit<MaintenanceSchedule, 'id' | 'createdAt'>): Promise<MaintenanceSchedule> {
    return this.request<MaintenanceSchedule>('/maintenance-schedules', { method: 'POST', body: JSON.stringify(ms) });
  }

  async updateMaintenanceSchedule(id: string, ms: Partial<MaintenanceSchedule>): Promise<MaintenanceSchedule> {
    return this.request<MaintenanceSchedule>(`/maintenance-schedules/${id}`, { method: 'PUT', body: JSON.stringify(ms) });
  }

  async completeMaintenanceSchedule(id: string): Promise<MaintenanceSchedule> {
    return this.request<MaintenanceSchedule>(`/maintenance-schedules/${id}/complete`, { method: 'PUT' });
  }

  async setMaintenanceScheduleStatus(id: string, status: string): Promise<MaintenanceSchedule> {
    return this.request<MaintenanceSchedule>(`/maintenance-schedules/${id}/status?status=${status}`, { method: 'PUT' });
  }

  async deleteMaintenanceSchedule(id: string): Promise<void> {
    return this.request<void>(`/maintenance-schedules/${id}`, { method: 'DELETE' });
  }

  // ============ VIOLATION FINE MANAGEMENT (S9.3) ============

  async applyViolationFine(id: string, amount: number): Promise<any> {
    return this.request<any>(`/condo/violations/${id}/fine`, { method: 'POST', body: JSON.stringify({ amount }) });
  }

  async markViolationFinePaid(id: string): Promise<any> {
    return this.request<any>(`/condo/violations/${id}/fine-paid`, { method: 'POST' });
  }

  // ============ WORK ORDER VENDOR/COST (S9.4) ============

  async assignWorkOrderVendor(id: string, vendorId: string, vendorName: string, vendorContact?: string): Promise<any> {
    return this.request<any>(`/work-orders/${id}/assign-vendor`, {
      method: 'POST',
      body: JSON.stringify({ vendorId, vendorName, vendorContact }),
    });
  }

  async signOffWorkOrder(id: string, signedOffBy: string): Promise<any> {
    return this.request<any>(`/work-orders/${id}/sign-off`, {
      method: 'POST',
      body: JSON.stringify({ signedOffBy }),
    });
  }

  async updateWorkOrderCosts(id: string, laborCost: number, materialCost: number, invoiceNumber?: string): Promise<any> {
    return this.request<any>(`/work-orders/${id}/costs`, {
      method: 'POST',
      body: JSON.stringify({ laborCost, materialCost, invoiceNumber }),
    });
  }

  // ============ UNITS (S10.1) ============
  // Existing createUnit/updateUnit/deleteUnit/updateUnitStatus/assignOwnerToUnit are defined above.

  async fetchUnitsForCondo(condoId: string): Promise<any[]> {
    return this.request<any[]>(`/units/condo/${condoId}`);
  }

  // ============ BOARD E-VOTING (S10.2) ============

  async castVote(resolutionId: string, vote: 'FOR' | 'AGAINST' | 'ABSTAIN'): Promise<any> {
    return this.request<any>(`/board-resolutions/${resolutionId}/cast-vote`, {
      method: 'POST',
      body: JSON.stringify({ vote }),
    });
  }

  // ============ MAINTENANCE → WORK ORDER PIPELINE (S10.3) ============

  async createWorkOrderFromRequest(requestId: string): Promise<any> {
    return this.request<any>(`/condo/maintenance-requests/${requestId}/create-work-order`, {
      method: 'POST',
    });
  }

  // ============ FINANCIAL REPORTS (S10.4) ============
  // getArAgingReport, getDelinquencyReport, getIncomeStatement, getBudgetVariance, getBalanceSheet
  // are all defined in the REPORTING section above.

}
